'use client';

/**
 * Guest-facing loyalty balance and redemption.
 *
 * Identity here is deliberately account-free: a phone number identifies you, and
 * a passkey lets the device recognise you silently next time. The page is two
 * states, not one long form , "who are you" and then "here's what you have" ,
 * because the previous single card showed an empty redemption stepper and a
 * passkey button to someone who hadn't identified themselves yet.
 *
 * Note for a follow-up: redemption value is rendered with a hardcoded `$`. The
 * loyalty status endpoint doesn't return a currency, so it isn't fixable from
 * here without an API change , left as-is rather than guessing a symbol.
 */

import { useCallback, useState } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { BadgeCheck } from 'lucide-react';
import { toast } from 'sonner';

import { useLoadingState } from '@/hooks/useLoadingState';
import {
  CustomerSection,
  CustomerSurface,
  CustomerSurfaceHeader,
  CustomerSurfaceStatus,
} from '@/components/customer-surface/CustomerSurface';
import { LoyaltyIdentifyForm } from '@/components/customer-surface/loyalty/LoyaltyIdentifyForm';
import { BalanceHero } from '@/components/customer-surface/loyalty/BalanceHero';
import { PointsRedeemer } from '@/components/customer-surface/loyalty/PointsRedeemer';
import { PasskeySetup } from '@/components/customer-surface/loyalty/PasskeySetup';
import { SwitchNumberButton } from '@/components/customer-surface/loyalty/SwitchNumberButton';

export default function LoyaltyPageClient() {
  const params = useParams<{ businessId: string }>();
  const businessId = params.businessId;
  const router = useRouter();

  const [phoneNumber, setPhoneNumber] = useState('');
  const [balance, setBalance] = useState<number | null>(null);
  const [tier, setTier] = useState<string | null>(null);
  const [redemptionRate, setRedemptionRate] = useState<{ pointsPerUnit: number; valueAmount: number } | null>(null);
  const [redeemPoints, setRedeemPoints] = useState(0);
  const [redeemedMessage, setRedeemedMessage] = useState<string | null>(null);
  const [identifyError, setIdentifyError] = useState<string | null>(null);
  const { isLoading: loading, setIsLoading: setLoading } = useLoadingState(false);
  const { isLoading: redeeming, setIsLoading: setRedeeming } = useLoadingState(false);
  const { isLoading: passkeyBusy, setIsLoading: setPasskeyBusy } = useLoadingState(false);

  const identify = useCallback(async () => {
    if (!phoneNumber.trim()) {
      setIdentifyError('Enter the phone number you use here.');
      return;
    }
    setLoading(true);
    setIdentifyError(null);
    setRedeemedMessage(null);
    setRedeemPoints(0);
    try {
      const [identifyRes, settingsRes] = await Promise.all([
        fetch('/api/public/loyalty/identify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ businessId, phoneNumber }),
        }),
        fetch(`/api/public/loyalty/status?businessId=${encodeURIComponent(businessId)}`),
      ]);
      const data = await identifyRes.json();
      if (!data.ok) {
        setIdentifyError(data.error || 'We couldn’t find points for that number.');
        return;
      }
      setBalance(data.balance);
      setTier(data.tier);

      if (settingsRes.ok) {
        const settingsData = await settingsRes.json();
        if (settingsData.ok && settingsData.redemptionPointsPerUnit > 0) {
          setRedemptionRate({
            pointsPerUnit: settingsData.redemptionPointsPerUnit,
            valueAmount: settingsData.redemptionValueAmount,
          });
        }
      }
    } catch {
      setIdentifyError('Something went wrong looking that up. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [businessId, phoneNumber]);

  const registerPasskey = useCallback(async () => {
    setPasskeyBusy(true);
    try {
      const optionsRes = await fetch('/api/public/loyalty/passkey/register-options', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ businessId, phoneNumber }),
      });
      const optionsData = await optionsRes.json();
      if (!optionsData.ok) {
        toast.error(optionsData.error || 'Could not start passkey setup.');
        return;
      }

      const attestation = await startRegistration({ optionsJSON: optionsData.options });

      const verifyRes = await fetch('/api/public/loyalty/passkey/register-verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ businessId, phoneNumber, challengeId: optionsData.challengeId, response: attestation }),
      });
      const verifyData = await verifyRes.json();
      if (!verifyData.ok) {
        toast.error(verifyData.error || 'Could not save passkey.');
        return;
      }
      toast.success('Passkey saved , we’ll recognise you next time, no typing.');
    } catch {
      toast.error('Passkey setup failed or was cancelled.');
    } finally {
      setPasskeyBusy(false);
    }
  }, [businessId, phoneNumber]);

  const signInWithPasskey = useCallback(async () => {
    setPasskeyBusy(true);
    setIdentifyError(null);
    try {
      const optionsRes = await fetch('/api/public/loyalty/passkey/auth-options', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ businessId }),
      });
      const optionsData = await optionsRes.json();
      if (!optionsData.ok) {
        setIdentifyError(optionsData.error || 'No passkey found on this device , use your phone number below.');
        return;
      }

      const assertion = await startAuthentication({ optionsJSON: optionsData.options });

      const verifyRes = await fetch('/api/public/loyalty/passkey/auth-verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challengeId: optionsData.challengeId, response: assertion }),
      });
      const verifyData = await verifyRes.json();
      if (!verifyData.ok) {
        setIdentifyError(verifyData.error || 'We couldn’t recognise that passkey.');
        return;
      }
      setPhoneNumber(verifyData.phoneNumber);
      setBalance(verifyData.balance);
      setTier(verifyData.tier);
      toast.success('Welcome back!');
    } catch {
      setIdentifyError('Passkey sign-in was cancelled. You can use your phone number instead.');
    } finally {
      setPasskeyBusy(false);
    }
  }, [businessId]);

  const handleRedeem = useCallback(async () => {
    if (redeemPoints <= 0) return;
    setRedeeming(true);
    setRedeemedMessage(null);
    try {
      const res = await fetch('/api/public/loyalty/redeem', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ businessId, phoneNumber, pointsToRedeem: redeemPoints }),
      });
      const data = await res.json();
      if (!data.ok) {
        toast.error(data.error || 'Could not redeem points.');
        return;
      }
      setBalance(data.remainingBalance);
      setRedeemedMessage(
        `Redeemed , $${data.redemptionValue.toFixed(2)} off. You have ${data.remainingBalance} points left.`,
      );
      setRedeemPoints(0);
      toast.success(`$${data.redemptionValue.toFixed(2)} off , points redeemed!`);
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setRedeeming(false);
    }
  }, [businessId, phoneNumber, redeemPoints]);

  const identified = balance !== null;
  const canRedeem = identified && redemptionRate && redemptionRate.pointsPerUnit > 0;
  const redeemValue = redemptionRate
    ? (redeemPoints / redemptionRate.pointsPerUnit) * redemptionRate.valueAmount
    : 0;
  const walletValue =
    redemptionRate && redemptionRate.pointsPerUnit > 0 && balance != null
      ? (balance / redemptionRate.pointsPerUnit) * redemptionRate.valueAmount
      : null;

  return (
    <CustomerSurface>
      <CustomerSurfaceHeader onBack={() => router.back()} eyebrow="Rewards" title="Your loyalty points" />

      <div className="mx-auto w-full max-w-xl px-4 py-6 sm:px-6 sm:py-10">
        {!identified ? (
          <CustomerSection
            title="Check your points"
            description="No account, no password. Your phone number is enough, or use a passkey if you've set one up here before."
          >
            <LoyaltyIdentifyForm
              phoneNumber={phoneNumber}
              onPhoneNumberChange={setPhoneNumber}
              onSubmit={() => void identify()}
              onPasskeySignIn={() => void signInWithPasskey()}
              error={identifyError}
              loading={loading}
              passkeyBusy={passkeyBusy}
            />
          </CustomerSection>
        ) : (
          <div className="space-y-4">
            <BalanceHero
              balance={balance}
              tier={tier}
              walletValue={walletValue}
              redemptionRate={redemptionRate}
              formatMoney={(amount, currency) => `${currency ?? '$'} ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            />

            {redeemedMessage ? (
              <CustomerSurfaceStatus tone="success">
                <span className="flex items-center gap-2">
                  <BadgeCheck className="size-4 shrink-0" aria-hidden="true" />
                  {redeemedMessage}
                </span>
              </CustomerSurfaceStatus>
            ) : null}

            {canRedeem ? (
              <CustomerSection title="Spend your points" description="Pick an amount, then show the confirmation to staff.">
                <PointsRedeemer
                  balance={balance || 0}
                  redeemPoints={redeemPoints}
                  redeemValue={redeemValue}
                  redemptionRate={redemptionRate!}
                  onIncrement={() => setRedeemPoints(redeemPoints + (redemptionRate?.pointsPerUnit ?? 0))}
                  onDecrement={() => setRedeemPoints(Math.max(0, redeemPoints - (redemptionRate?.pointsPerUnit ?? 0)))}
                  onRedeem={() => void handleRedeem()}
                  loading={redeeming}
                />
              </CustomerSection>
            ) : (
              <CustomerSurfaceStatus tone="info">
                This location isn’t offering point redemption yet , your points keep adding up in the meantime.
              </CustomerSurfaceStatus>
            )}

            <PasskeySetup
              onRegister={() => void registerPasskey()}
              loading={passkeyBusy}
            />

            <SwitchNumberButton
              onClick={() => {
                setBalance(null);
                setTier(null);
                setRedeemPoints(0);
                setRedeemedMessage(null);
              }}
            />
          </div>
        )}
      </div>
    </CustomerSurface>
  );
}
